/**
 * DeviceLogsTab — Searchable, filterable, sortable, paginated device log table.
 *
 * Visible only for users with roleWeight >= 80.
 * Columns: Timestamp (UTC→local), Log Event (with header filter)
 */

import { useState, useMemo, useCallback } from 'react';
import { convertIsoToLocal } from '../../../../libs.jsx';
import usePersistedPerPage from '../../../../hooks/usePersistedPerPage.js';

const DeviceLogsTab = ({ logData, tzOffset }) => {
  // Search
  const [search, setSearch] = useState('');

  // Log event type filter
  const [logTypeFilter, setLogTypeFilter] = useState('');

  // Sorting
  const [sortCol, setSortCol] = useState('create_time');
  const [sortDir, setSortDir] = useState('desc');

  // Pagination
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = usePersistedPerPage();

  // Unique log event types for the filter dropdown
  const logTypes = useMemo(() => {
    const types = new Set();
    (logData || []).forEach((row) => {
      if (row.device_log_type_name) types.add(row.device_log_type_name);
    });
    return [...types].sort();
  }, [logData]);

  // Filter + sort + paginate
  const filtered = useMemo(() => {
    let rows = [...(logData || [])];

    // Search filter
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter((r) =>
        (r.device_log_type_name || '').toLowerCase().includes(q) ||
        (r.create_time || '').toLowerCase().includes(q)
      );
    }

    // Log type filter
    if (logTypeFilter) {
      rows = rows.filter((r) => r.device_log_type_name === logTypeFilter);
    }

    // Sort
    rows.sort((a, b) => {
      const aVal = (a[sortCol] || '').toString().toLowerCase();
      const bVal = (b[sortCol] || '').toString().toLowerCase();
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return rows;
  }, [logData, search, logTypeFilter, sortCol, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageRows = filtered.slice((page - 1) * perPage, page * perPage);

  const handleSort = useCallback((col) => {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
    setPage(1);
  }, [sortCol]);

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <span className="dp-sort-icon dp-sort-icon--neutral dp-icon-xs">↕</span>;
    return <span className="dp-sort-icon dp-sort-icon--active dp-icon-xs">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  // Convert UTC timestamp to local using the tzOffset
  const formatTimestamp = (ts) => {
    if (!ts) return '—';
    return convertIsoToLocal(ts, tzOffset);
  };

  return (
    <div className="pp-section">
      <div className="pp-section__header">
        <h3 className="pp-section__title">Device Logs</h3>
        <div className="pp-section__actions">
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
            {filtered.length} entries
          </span>
        </div>
      </div>
      <div className="pp-section__body">
        {/* Controls */}
        <div className="dp-controls">
          <div className="dp-controls__left">
            <div className="dp-search">
              <svg className="dp-icon-md dp-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
              <input
                className="dp-search__input"
                type="text"
                placeholder="Search logs…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
            </div>
            {logTypes.length > 0 && (
              <select
                className="dp-select"
                value={logTypeFilter}
                onChange={(e) => { setLogTypeFilter(e.target.value); setPage(1); }}
                style={{ maxWidth: '200px' }}
              >
                <option value="">All Log Types</option>
                {logTypes.map((lt) => (
                  <option key={lt} value={lt}>{lt}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Table */}
        {logData.length === 0 ? (
          <div className="dp-empty" style={{ marginTop: 'var(--space-4)' }}>
            <p className="dp-empty__title">No device logs</p>
            <p className="dp-empty__text">No log entries are available for this device.</p>
          </div>
        ) : (
          <>
            <div className="dp-table-wrap" style={{ marginTop: 'var(--space-3)' }}>
              <table className="dp-table">
                <thead className="dp-thead">
                  <tr>
                    <th className="dp-th">
                      <div className="dp-th__content">
                        <button className="dp-th__sort-btn" onClick={() => handleSort('create_time')}>
                          Timestamp <SortIcon col="create_time" />
                        </button>
                      </div>
                    </th>
                    <th className="dp-th">
                      <div className="dp-th__content">
                        <button className="dp-th__sort-btn" onClick={() => handleSort('device_log_type_name')}>
                          Log Event <SortIcon col="device_log_type_name" />
                        </button>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((row, idx) => (
                    <tr key={idx} className="dp-tr">
                      <td className="dp-td">
                        <span className="dp-cell-text dp-cell-text--muted" style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-xs)' }}>
                          {formatTimestamp(row.create_time)}
                        </span>
                      </td>
                      <td className="dp-td">
                        <span className="dp-cell-text">{row.device_log_type_name || '—'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filtered.length > perPage && (
              <div className="dp-pagination">
                <span className="dp-pagination__info">
                  Showing <span className="dp-pagination__emphasis">{(page - 1) * perPage + 1}</span>
                  –<span className="dp-pagination__emphasis">{Math.min(page * perPage, filtered.length)}</span>
                  {' '}of <span className="dp-pagination__emphasis">{filtered.length}</span>
                </span>
                <div className="dp-pagination__controls">
                  <div className="dp-per-page">
                    <span className="dp-per-page__label">Per page:</span>
                    <select className="dp-per-page__select" value={perPage} onChange={(e) => { setPerPage(+e.target.value); setPage(1); }}>
                      {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div className="dp-page-nav">
                    <button className="dp-page-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹</button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).slice(
                      Math.max(0, page - 3), Math.min(totalPages, page + 2)
                    ).map((p) => (
                      <button
                        key={p}
                        className={`dp-page-num${p === page ? ' is-active' : ''}`}
                        onClick={() => setPage(p)}
                      >
                        {p}
                      </button>
                    ))}
                    <button className="dp-page-btn" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>›</button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DeviceLogsTab;
